// Session-token transport helpers. The Redis session token travels in the
// `Authorization: Bearer <sessionID>` header, never in the URL. These pure
// helpers are used by ApiClient's axios interceptor + fetch patch (and are
// unit-tested here without ApiClient's import-time side effects).
import { LS_SESSION_ID } from "../constants/storageKeys";

/**
 * Current Redis session token from localStorage, or "" when signed out.
 *
 * The value is JSON-encoded on the way in: LoginPage stores it through
 * `useLocalStorage`, which `JSON.stringify`s, so the raw item is `"abc123"`
 * WITH the quotes. Reading it raw (7HAI-923) produced
 * `Authorization: Bearer "abc123"` — a token the backend cannot match, so the
 * request came back 401 and the user was bounced to the login page mid-session
 * for no reason they could see. The un-quoting is tolerant on purpose: a token
 * written by any other path (or by an older build) is still a plain string, and
 * both must work.
 */
export const currentSession = (): string => {
  try {
    const raw = localStorage.getItem(LS_SESSION_ID);
    if (!raw) return "";
    try {
      const parsed: unknown = JSON.parse(raw);
      return typeof parsed === "string" ? parsed : "";
    } catch {
      return raw; // not JSON — a plain token written by some other path
    }
  } catch {
    return "";
  }
};

/**
 * The one way to authenticate a hand-written `fetch` call.
 *
 * Spread into a fetch init's `headers`. Call sites pass the token they already
 * hold (from `useLocalStorage`); it falls back to localStorage so a caller that
 * has none still authenticates. Returns an EMPTY object when there is no token,
 * so a signed-out caller sends no `Authorization` at all and gets the backend's
 * 404 rather than a 401 for the literal string "undefined".
 *
 * Exists so no call site has to think about the transport: before 7HAI-923 the
 * hand-written fetch hooks each built `?sessionID=` into the URL and relied on
 * ApiClient's fetch patch to move it to a header afterwards — which silently did
 * nothing whenever that module had not been imported yet.
 */
export const sessionHeader = (token?: string): Record<string, string> => {
  const sid = token || currentSession();
  return sid ? { Authorization: `Bearer ${sid}` } : {};
};

/**
 * Remove a `sessionID` query param from a URL, returning the cleaned URL and
 * the token it carried. URLSearchParams handles decoding and preserves the
 * other params; a URL with no other params loses its `?` entirely.
 */
export const stripSessionId = (rawUrl: string): { url: string; token: string } => {
  const q = rawUrl.indexOf("?");
  if (q === -1) return { url: rawUrl, token: "" };
  const params = new URLSearchParams(rawUrl.slice(q + 1));
  const token = params.get("sessionID") ?? "";
  if (!token) return { url: rawUrl, token: "" };
  params.delete("sessionID");
  const qs = params.toString();
  return { url: qs ? `${rawUrl.slice(0, q)}?${qs}` : rawUrl.slice(0, q), token };
};
